import { MenuItem, Checkbox } from '@page/ui';

import { SelectOption } from './Select';

interface CheckboxMenuItemProps<T> {
  option: SelectOption<T | null | undefined>;
  isChecked: boolean;
  disabled?: boolean;
  onItemClick: (option: SelectOption<T | null | undefined>, checked: boolean) => void;
}

function CheckboxMenuItem<T>({ option, isChecked, disabled, onItemClick }: CheckboxMenuItemProps<T>) {
  return (
    <div className="flex flex-col">
      <MenuItem
        key={String(option.value)}
        className="w-full group [&_*]:pointer-events-none"
        disabled={option.disabled || disabled}
        onClick={() => {
          onItemClick(option, !isChecked);
        }}
      >
        <Checkbox
          checked={isChecked}
          label={option.label}
          disabled={option.disabled || disabled}
          description={option.description}
        />
      </MenuItem>

      {option.additionalContent}
    </div>
  );
}

interface CheckboxMenuItemsProps<T> {
  options: SelectOption<T | null | undefined>[];
  values?: T[];
  onValuesChange?: (values: T[]) => void;
  onItemClick?: (option: SelectOption<T | null | undefined>, checked: boolean) => void;
}

export function CheckboxMenuItems<T>({ options, values, onValuesChange, onItemClick }: CheckboxMenuItemsProps<T>) {
  const handleMultipleChange = (optionValue: T | null | undefined, checked: boolean) => {
    if (!values || !onValuesChange) return;

    if (checked) {
      if (optionValue !== null && optionValue !== undefined) {
        onValuesChange([...values, optionValue]);
      }
    } else {
      if (optionValue !== null && optionValue !== undefined) {
        onValuesChange(values.filter((v) => v !== optionValue));
      }
    }
  };

  const handleItemClick = (option: SelectOption<T | null | undefined>, checked: boolean) => {
    onItemClick?.(option, checked);
    handleMultipleChange(option.value, checked);
  };

  return (
    <div className="gap-0 overflow-hidden [&_label]:font-semibold [&_label]:truncate [&_label]:cursor-pointer [&_label]:w-full">
      {options.map((option) => {
        const isChecked = values?.includes(option.value as T) || false;

        return (
          <CheckboxMenuItem
            key={String(option.value)}
            option={option}
            isChecked={isChecked}
            onItemClick={handleItemClick}
          />
        );
      })}
    </div>
  );
}
